<script setup lang="ts">
// #739: /newtop（#737 プレビュー）を本 `/` に昇格したもの。旧 V1 部品
// （HeroSection/MakeSection/HowToSection）は退役・削除済み。
const runtimeConfig = useRuntimeConfig()
const { trackClassroomCta } = useFunnelEvent()

useHead({
  link: [
    { rel: 'canonical', href: `${runtimeConfig.public.serviceDomain}/` },
    // #737: Pyxel/Pyodide（cdn.jsdelivr.net）への接続を先に温める。ヒーローの
    // 「動かしてみる」を押した瞬間のロード開始を速くするための即効策（load-on-intent と併用）。
    { rel: 'preconnect', href: 'https://cdn.jsdelivr.net', crossorigin: '' },
    { rel: 'dns-prefetch', href: 'https://cdn.jsdelivr.net' },
  ],
})

useSeoMeta({
  title: 'Code & Magic | 動くまで打ち込んだ、あの夜の続きを — Python×Pyxel レトロゲームプログラミング',
  description:
    '動くまで打ち込んだ、あの夜の続きを。触れば動く、紙とウェブのプログラミング体験。',
  ogTitle: 'Code & Magic | 動くまで打ち込んだ、あの夜の続きを — Python×Pyxel レトロゲームプログラミング',
  ogDescription:
    '動くまで打ち込んだ、あの夜の続きを。触れば動く、紙とウェブのプログラミング体験。',
  ogImage: `${runtimeConfig.public.serviceDomain}/images/ogPlayMakeShare_wLogo.webp`,
  twitterCard: 'summary_large_image',
})

// JSON-LD: SoftwareApplication + Organization
useJsonLd({
  '@context': 'https://schema.org',
  '@type': 'SoftwareApplication',
  'name': 'Code & Magic',
  'description': '動くまで打ち込んだ、あの夜の続きを。触れば動く、紙とウェブのプログラミング体験。',
  'url': runtimeConfig.public.serviceDomain,
  'applicationCategory': 'EducationalApplication',
  'operatingSystem': 'Web browser',
  'offers': {
    '@type': 'Offer',
    'price': '0',
    'priceCurrency': 'JPY',
    'description': 'フリープラン',
  },
  'educationalAlignment': {
    '@type': 'AlignmentObject',
    'alignmentType': 'educationalSubject',
    'targetName': 'プログラミング',
  },
  'publisher': {
    '@type': 'Organization',
    'name': 'サムライアプス株式会社',
    'url': 'https://www.samurai-apps.com/',
    'email': 'support@samurai-apps.com',
    'address': {
      '@type': 'PostalAddress',
      'addressCountry': 'JP',
      'postalCode': '220-0004',
      'addressRegion': '神奈川県',
      'addressLocality': '横浜市西区',
      'streetAddress': '北幸2丁目10番48号むつみビル3階',
    },
  },
})

definePageMeta({
  title: 'Home',
  description: 'This is the home page',
  layout: 'landing',
})

// #737: あそぶセクションのサブコピー（\n で改行）
const playSubtitle = '集まった作品を、ブラウザひとつですぐ遊べる。\nつくらなくても、あそぶだけでOK。'

// #737: 教育セクション。主役はあくまで「大人のあなた」。大人が夢中になれることは
// 子供もきっと楽しい、という柔らかい招待のトーンにする（子供向けサービスに見えないように）。
const eduTitle = '子供だって、きっと楽しい'
const eduIntro = [
  'あなたが夢中になれることは、お子さんもきっと楽しい。よかったら、一緒にどうですか？',
]
</script>

<template>
  <v-container class="container">
    <section
      id="hero"
      class="pt-0"
    >
      <LandingHeroSectionV2 />
    </section>

    <!-- 遊べるギャラリーを最上部へ昇格（C）。あそぶだけでもOKの論調に -->
    <section id="play">
      <LandingPlaySection :subtitle="playSubtitle" />
    </section>

    <!-- つくる：自由に作って公開できる（ゆりかご→紙の魔導書の順） -->
    <section id="make">
      <LandingMakeSectionV2 />
    </section>

    <!-- Code & Magic とは（Python × Pyxel へのリスペクト）。あそぶ→つくるのリズムを崩さず直後に置く。
         見出しあり／ゲーム仕立て小節・画面キャプチャはカット -->
    <section id="about">
      <LandingAboutSection
        show-heading
        :show-game-like="false"
        :show-images="false"
      />
    </section>

    <!-- 利用の流れ：あそぶ/つくる/学ぶ/公開を包含 -->
    <section id="howto">
      <LandingHowToSectionV2 />
    </section>

    <!-- 無料で始められる安心（料金）。直上に大人の声を置いて「払う価値」を後押し -->
    <section id="pricing">
      <LandingVoiceCard id="ta02" />
      <LandingPricingSection />
    </section>

    <!-- 疑問の解消 -->
    <section id="faq">
      <LandingFaqSection />
    </section>

    <!-- 大人も夢中なら子供だって楽しい（証明）。子供カルーセルは FAQ の後に配置 -->
    <section id="education">
      <LandingForEducationSection
        :title="eduTitle"
        :intro="eduIntro"
      />
    </section>

    <!-- #920: 教室・学校向けの入口。子供カルーセル（教育文脈）の直後に置く。
         一等地は使わない —— 先生は「探して見つける」層で、B2C の導線を薄めないため。 -->
    <section id="classroom">
      <div class="classroom-band">
        <p class="classroom-band-text">
          教室や学校のクラスで使いたい方へ。
        </p>
        <NuxtLink
          to="/classroom"
          class="classroom-band-link"
          @click="trackClassroomCta('entry', 'top')"
        >
          教室でのご利用について
        </NuxtLink>
      </div>
    </section>

    <!-- 最後の一押し＋SNS -->
    <section
      id="follow"
      class="pb-0"
    >
      <LandingFollowNowSection />
    </section>
  </v-container>
</template>

<style scoped>
.container {
  background-color: var(--landing-color-parchment);

  font-family: "Noto Sans JP", sans-serif !important;
  color: var(--landing-color-text);

  margin-left: 0;
  width: 100%;
  max-width: 100%;
  padding: 0;
}

@media (max-width: 600px) {
  section {
    padding-top: 16px;
    padding-bottom: 16px;
  }
}

@media (min-width: 600.02px) {
  section {
    padding-top: 32px;
    padding-bottom: 32px;
  }
}

/* #920: 教室・学校向けの導線。目立たせすぎない（B2C の導線を薄めない） */
.classroom-band {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.75rem;
  text-align: center;
}

.classroom-band-text {
  margin: 0;
  font-size: 0.95rem;
  color: var(--landing-color-text);
}

.classroom-band-link {
  color: var(--landing-color-navy);
  font-size: 0.95rem;
  font-weight: 700;
  text-decoration: underline;
}

section.black {
  background-color: var(--landing-color-dark-bg);
  color: white;
}

/* ヒーローは全画面。上部の余白を殺してファーストビューを最大化 */
#hero {
  padding-top: 0 !important;
  padding-bottom: 0 !important;
}

/* #737: モバイルで料金/FAQ の白カード左右マージンを他セクション(利用の流れ等)と揃える。
   これらは共有部品の .rounded-area に margin:0 0.5em があり、他より 0.5em ずつ内側に入っていた。 */
@media (max-width: 600px) {
  #pricing :deep(.rounded-area),
  #faq :deep(.rounded-area) {
    margin-left: 0;
    margin-right: 0;
  }
}
</style>
